import { createClient } from "@/app/utils/supabase/client";
import { STORAGE_BUCKETS, STORAGE_PATHS, STORAGE_LIMITS, ALLOWED_MIME_TYPES } from "../supabase/supabaseStorageConstants";
import { ImageCompressionService } from "../services/imageCompression";

export interface UploadOptions {
  onProgress?: (progress: number) => void;
  validateSize?: boolean;
  validateType?: boolean;
  compress?: boolean;
  compressionPreset?: 'thumbnail' | 'reference' | 'document' | 'highQuality';
}

export interface StorageFile {
  name: string;
  size: number;
  type: string;
  lastModified: number;
  url?: string;
}

export const storageApi = {
  // Validate file before upload
  validateFile(file: File, bucketType: keyof typeof STORAGE_LIMITS): { valid: boolean; error?: string } {
    // Size validation
    const sizeLimit = STORAGE_LIMITS[bucketType];
    if (file.size > sizeLimit) {
      return {
        valid: false,
        error: `File size exceeds limit of ${sizeLimit / 1024 / 1024}MB`
      };
    }

    // Type validation
    const allowedTypes = ALLOWED_MIME_TYPES[bucketType] as readonly string[];
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `File type ${file.type} is not allowed`
      };
    }

    return { valid: true };
  },

  // Sanitize filename for Supabase storage
  sanitizeFilename(filename: string): string {
    // Get file extension
    const lastDotIndex = filename.lastIndexOf('.');
    const extension = lastDotIndex !== -1 ? filename.slice(lastDotIndex) : '';
    const nameWithoutExt = lastDotIndex !== -1 ? filename.slice(0, lastDotIndex) : filename;
    
    // Sanitize the filename
    const sanitized = nameWithoutExt
      .replace(/[—–]/g, '-')           // Replace em dashes and en dashes with regular hyphen
      .replace(/[^\w\-]/g, '_')        // Replace any non-alphanumeric (except hyphen) with underscore
      .replace(/_{2,}/g, '_')          // Replace multiple underscores with single
      .replace(/^[_\-]+|[_\-]+$/g, '') // Remove leading/trailing underscores or hyphens
      .toLowerCase();                   // Convert to lowercase for consistency
    
    // Ensure filename is not empty after sanitization
    const finalName = sanitized || 'file';
    
    return finalName + extension;
  },

  // Generate unique filename
  generateFilename(originalName: string): string {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8);
    
    // Sanitize the original filename first
    const sanitizedOriginal = this.sanitizeFilename(originalName);
    
    // Get extension from sanitized filename
    const lastDotIndex = sanitizedOriginal.lastIndexOf('.');
    const extension = lastDotIndex !== -1 ? sanitizedOriginal.slice(lastDotIndex) : '';
    const nameWithoutExt = lastDotIndex !== -1 ? sanitizedOriginal.slice(0, lastDotIndex) : sanitizedOriginal;
    
    // Limit the filename length to prevent overly long names
    const truncatedName = nameWithoutExt.slice(0, 50);
    
    return `${timestamp}-${randomStr}-${truncatedName}${extension}`;
  },

  // Upload project thumbnail
  async uploadProjectThumbnail(
    companyId: string,
    projectId: string,
    file: File,
    options?: UploadOptions
  ) {
    // Compress image if it's an image file and compression is enabled (default: true)
    let fileToUpload = file;
    const shouldCompress = options?.compress !== false && file.type.startsWith('image/');
    
    if (shouldCompress) {
      try {
        const compressionOptions = ImageCompressionService.getOptimalOptions(
          file,
          options?.compressionPreset || 'thumbnail'
        );
        
        const compressionResult = await ImageCompressionService.compressImage(
          file,
          compressionOptions
        );
        
        // Only use compressed version if it's significantly smaller
        if (ImageCompressionService.isCompressionWorthwhile(compressionResult)) {
          fileToUpload = compressionResult.file;
          console.log(`<<<<<<<<<< Thumbnail compression successful:`);
          console.log(`<<<<<<<<<< Original size: ${(file.size / 1024).toFixed(2)}KB`);
          console.log(`<<<<<<<<<< Compressed size: ${(fileToUpload.size / 1024).toFixed(2)}KB`);
          console.log(`<<<<<<<<<< Size reduction: ${Math.round((1 - compressionResult.compressionRatio) * 100)}%`);
          console.log(`<<<<<<<<<< Dimensions: ${compressionResult.width}x${compressionResult.height}`);
        } else {
          console.log(`<<<<<<<<<< Compression not worthwhile for thumbnail, using original`);
          console.log(`<<<<<<<<<< Original size: ${(file.size / 1024).toFixed(2)}KB`);
        }
      } catch (error) {
        console.warn('Image compression failed, uploading original:', error);
        // Continue with original file if compression fails
      }
    }

    const validation = this.validateFile(fileToUpload, 'PUBLIC_PROJECT_THUMBNAILS');
    if (!validation.valid) throw new Error(validation.error);

    const supabase = createClient();
    const filename = this.generateFilename(fileToUpload.name);
    const path = STORAGE_PATHS.projectThumbnail(companyId, projectId, filename);

    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.PUBLIC_PROJECT_THUMBNAILS)
      .upload(path, fileToUpload, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(STORAGE_BUCKETS.PUBLIC_PROJECT_THUMBNAILS)
      .getPublicUrl(path);

    return { path: data.path, publicUrl };
  },

  // Upload project document
  async uploadProjectDocument(
    companyId: string,
    projectId: string,
    file: File,
  ) {
    const validation = this.validateFile(file, 'COMPANY_DOCUMENTS');
    if (!validation.valid) throw new Error(validation.error);

    const supabase = createClient();
    const filename = this.generateFilename(file.name);
    const path = STORAGE_PATHS.projectDocument(projectId, filename);
    const bucket = STORAGE_BUCKETS.COMPANY_DOCUMENTS(companyId);

    console.log("Uploading project document to:", path, bucket);

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    return {
      path: data.path,
      bucket,
      filename: file.name,
      size: file.size,
      type: file.type
    };
  },

  // Upload quality check question reference image
  async uploadQuestionReferenceImage(
    companyId: string,
    projectId: string,
    stageId: string,
    questionUuid: string,
    file: File,
    options?: UploadOptions
  ) {
    // Compress image if it's an image file and compression is enabled (default: true)
    let fileToUpload = file;
    const shouldCompress = options?.compress !== false && file.type.startsWith('image/');
    
    if (shouldCompress) {
      try {
        const compressionOptions = ImageCompressionService.getOptimalOptions(
          file,
          options?.compressionPreset || 'reference'
        );
        
        const compressionResult = await ImageCompressionService.compressImage(
          file,
          compressionOptions
        );
        
        // Only use compressed version if it's significantly smaller
        if (ImageCompressionService.isCompressionWorthwhile(compressionResult)) {
          fileToUpload = compressionResult.file;
          console.log(`<<<<<<<<<< Reference image compression successful:`);
          console.log(`<<<<<<<<<< Original size: ${(file.size / 1024).toFixed(2)}KB`);
          console.log(`<<<<<<<<<< Compressed size: ${(fileToUpload.size / 1024).toFixed(2)}KB`);
          console.log(`<<<<<<<<<< Size reduction: ${Math.round((1 - compressionResult.compressionRatio) * 100)}%`);
          console.log(`<<<<<<<<<< Dimensions: ${compressionResult.width}x${compressionResult.height}`);
        } else {
          console.log(`<<<<<<<<<< Compression not worthwhile for reference image, using original`);
          console.log(`<<<<<<<<<< Original size: ${(file.size / 1024).toFixed(2)}KB`);
        }
      } catch (error) {
        console.warn('Image compression failed, uploading original:', error);
        // Continue with original file if compression fails
      }
    }

    const validation = this.validateFile(fileToUpload, 'COMPANY_IMAGES');
    if (!validation.valid) throw new Error(validation.error);

    const supabase = createClient();
    const filename = this.generateFilename(fileToUpload.name);
    const path = STORAGE_PATHS.questionReferenceImage(projectId, stageId, questionUuid, filename);
    const bucket = STORAGE_BUCKETS.COMPANY_IMAGES(companyId);

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, fileToUpload, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      console.error("Error uploading question reference image", error);
      throw error;
    };

    return { path: data.path, bucket };
  },

  // Upload quality check answer image
  async uploadQualityCheckAnswerImage(
    companyId: string,
    projectId: string,
    qualityCheckId: string,
    questionId: string,
    file: File,
    options?: UploadOptions
  ): Promise<{ path: string; bucket: string }> {
    if (!companyId) throw new Error("No company selected");
    if (!projectId) throw new Error("No project selected");
    if (!qualityCheckId) throw new Error("No quality check selected");
    if (!questionId) throw new Error("No question selected");
    if (!file) throw new Error("No file selected");

    // Compress image if it's an image file and compression is enabled (default: true)
    let fileToUpload = file;
    const shouldCompress = options?.compress !== false && file.type.startsWith('image/');
    
    if (shouldCompress) {
      try {
        const compressionOptions = ImageCompressionService.getOptimalOptions(
          file,
          options?.compressionPreset || 'reference'
        );
        
        const compressionResult = await ImageCompressionService.compressImage(
          file,
          compressionOptions
        );
        
        // Only use compressed version if it's significantly smaller
        if (ImageCompressionService.isCompressionWorthwhile(compressionResult)) {
          fileToUpload = compressionResult.file;
          console.log(`<<<<<<<<<< Answer image compression successful:`);
          console.log(`<<<<<<<<<< Original size: ${(file.size / 1024).toFixed(2)}KB`);
          console.log(`<<<<<<<<<< Compressed size: ${(fileToUpload.size / 1024).toFixed(2)}KB`);
          console.log(`<<<<<<<<<< Size reduction: ${Math.round((1 - compressionResult.compressionRatio) * 100)}%`);
          console.log(`<<<<<<<<<< Dimensions: ${compressionResult.width}x${compressionResult.height}`);
        } else {
          console.log(`<<<<<<<<<< Compression not worthwhile for answer image, using original`);
          console.log(`<<<<<<<<<< Original size: ${(file.size / 1024).toFixed(2)}KB`);
        }
      } catch (error) {
        console.warn('Image compression failed, uploading original:', error);
        // Continue with original file if compression fails
      }
    }

    const validation = this.validateFile(fileToUpload, 'COMPANY_IMAGES');
    if (!validation.valid) throw new Error(validation.error);
    const supabase = createClient();
    const filename = this.generateFilename(fileToUpload.name);
    const path = STORAGE_PATHS.qualityCheckAnswerImage(projectId, qualityCheckId, questionId, filename);
    const bucket = STORAGE_BUCKETS.COMPANY_IMAGES(companyId);

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, fileToUpload, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      console.error("Error uploading quality check answer image", error);
      throw error;
    };

    return { path: data.path, bucket };
  },

  // Upload task attachment
  async uploadTaskAttachment(
    companyId: string,
    taskId: string,
    file: File,
  ) {
    const validation = this.validateFile(file, 'COMPANY_DOCUMENTS');
    if (!validation.valid) throw new Error(validation.error);

    const supabase = createClient();
    const filename = this.generateFilename(file.name);
    const path = STORAGE_PATHS.taskAttachment(taskId, filename);
    const bucket = STORAGE_BUCKETS.COMPANY_DOCUMENTS(companyId);

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      console.error(`Error uploading task attachment, path: ${path}, bucket: ${bucket}`, error);
      throw error;
    };

    return { path: data.path, bucket };
  },

  // Upload follow-up communication attachment
  async uploadFollowUpCommunicationAttachment(
    companyId: string,
    threadId: string,
    communicationId: string,
    file: File,
  ) {
    const validation = this.validateFile(file, 'COMPANY_DOCUMENTS');
    if (!validation.valid) throw new Error(validation.error);

    const supabase = createClient();
    const filename = this.generateFilename(file.name);
    const path = STORAGE_PATHS.followUpCommunicationAttachment(
      threadId,
      communicationId,
      filename
    );
    const bucket = STORAGE_BUCKETS.COMPANY_DOCUMENTS(companyId);

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    return {
      path: data.path,
      bucket,
      filename: file.name,
      size: file.size,
      type: file.type
    };
  },

  // Upload email attachment
  async uploadEmailAttachment(
    companyId: string,
    emailId: string, // This should be a UUID generated before upload
    file: File,
  ) {
    const validation = this.validateFile(file, 'COMPANY_DOCUMENTS');
    if (!validation.valid) throw new Error(validation.error);

    const supabase = createClient();
    const filename = this.generateFilename(file.name);
    const path = STORAGE_PATHS.emailAttachment(emailId, filename);
    const bucket = STORAGE_BUCKETS.COMPANY_DOCUMENTS(companyId);

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    return {
      path: data.path,
      bucket,
      filename: file.name,
      originalFilename: file.name,
      size: file.size,
      type: file.type,
      contentType: file.type,
    };
  },

  // Get signed URL for private files
  async getSignedUrl(bucket: string, path: string, expiresIn = 3600) {
    const supabase = createClient();

    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn);

    if (error) throw error;

    return data.signedUrl;
  },

  // Get public URL for public files
  getPublicUrl(bucket: string, path: string) {
    const supabase = createClient();

    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);

    return data.publicUrl;
  },

  // Download file
  async downloadFile(bucket: string, path: string) {
    const supabase = createClient();

    const { data, error } = await supabase.storage
      .from(bucket)
      .download(path);

    if (error) throw error;

    return data;
  },

  // Delete file
  async deleteFile(bucket: string, path: string) {
    const supabase = createClient();

    const { error } = await supabase.storage
      .from(bucket)
      .remove([path]);

    if (error) throw error;
  },

  // Delete multiple files
  async deleteFiles(bucket: string, paths: string[]) {
    const supabase = createClient();

    const { error } = await supabase.storage
      .from(bucket)
      .remove(paths);

    if (error) throw error;
  },

  // Helper method to compress multiple images before upload
  async compressImagesForUpload(
    files: File[],
    preset: 'thumbnail' | 'reference' | 'document' | 'highQuality' = 'reference',
    onProgress?: (completed: number, total: number) => void
  ): Promise<File[]> {
    const processedFiles: File[] = [];
    let completed = 0;

    for (const file of files) {
      // Only compress image files
      if (file.type.startsWith('image/')) {
        try {
          const compressionOptions = ImageCompressionService.getOptimalOptions(file, preset);
          const result = await ImageCompressionService.compressImage(file, compressionOptions);
          
          // Use compressed version if it's worthwhile
          if (ImageCompressionService.isCompressionWorthwhile(result)) {
            processedFiles.push(result.file);
            console.log(`<<<<<<<<<< Batch compression - ${file.name}:`);
            console.log(`<<<<<<<<<< Original: ${(file.size / 1024).toFixed(2)}KB → Compressed: ${(result.file.size / 1024).toFixed(2)}KB`);
            console.log(`<<<<<<<<<< Reduction: ${Math.round((1 - result.compressionRatio) * 100)}%`);
          } else {
            processedFiles.push(file);
            console.log(`<<<<<<<<<< Batch - ${file.name}: compression not worthwhile, using original (${(file.size / 1024).toFixed(2)}KB)`);
          }
        } catch (error) {
          console.warn(`Failed to compress ${file.name}, using original:`, error);
          processedFiles.push(file);
        }
      } else {
        // Non-image files pass through unchanged
        processedFiles.push(file);
      }

      completed++;
      if (onProgress) {
        onProgress(completed, files.length);
      }
    }

    return processedFiles;
  },

  // List files in a directory
  async listFiles(bucket: string, path: string, options?: { limit?: number; offset?: number }) {
    const supabase = createClient();

    const { data, error } = await supabase.storage
      .from(bucket)
      .list(path, {
        limit: options?.limit || 100,
        offset: options?.offset || 0,
      });

    if (error) throw error;

    return data;
  },

  // Create company buckets
  async createCompanyBuckets(companyId: string) {

    // Note: Bucket creation requires service role key
    // This should be done server-side or through an edge function
    // For now, we'll return the bucket names that should be created

    return {
      documentsBucket: STORAGE_BUCKETS.COMPANY_DOCUMENTS(companyId),
      imagesBucket: STORAGE_BUCKETS.COMPANY_IMAGES(companyId),
      message: 'Buckets should be created server-side with service role key'
    };
  }
};